import { Plugin } from "prosemirror-state";
// Utility functions
const readBlobAsDataURL = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});
const fetchImageAsDataURL = async (url) => {
    const response = await fetch(url);
    if (!response.ok)
        throw new Error("Image fetch failed");
    const blob = await response.blob();
    return readBlobAsDataURL(blob);
};
const processImageElement = async (img) => {
    try {
        const src = img.src.startsWith("data:")
            ? img.src
            : await fetchImageAsDataURL(img.src);
        return { src, alt: img.alt || "Pasted image" };
    }
    catch (error) {
        console.error("Image processing error:", error);
        return null;
    }
};
const createImageNode = (view, src, alt) => view.state.schema.nodes.image.create({ src, alt });
const insertImages = (view, images, pos) => {
    const transaction = view.state.tr;
    images.forEach(({ src, alt }) => {
        const node = createImageNode(view, src, alt);
        pos !== undefined
            ? transaction.insert(pos, node)
            : transaction.replaceSelectionWith(node);
    });
    view.dispatch(transaction);
};
const handleHtmlContent = async (view, html, pos) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const images = Array.from(doc.querySelectorAll("img"));
    if (!images.length)
        return false;
    const results = await Promise.all(images.map(processImageElement));
    const validResults = results.filter(Boolean);
    if (!validResults.length)
        return false;
    insertImages(view, validResults, pos);
    return true;
};
const handleFileContent = async (view, files, pos) => {
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    if (!imageFiles.length)
        return false;
    const dataUrls = await Promise.all(imageFiles.map(readBlobAsDataURL));
    const images = dataUrls.map((src, i) => ({
        src,
        alt: pos !== undefined
            ? imageFiles[i].name || "Dropped image"
            : "Pasted image",
    }));
    insertImages(view, images, pos);
    return true;
};
const getDropPosition = (view, event) => {
    const coordinates = view.posAtCoords({
        left: event.clientX,
        top: event.clientY,
    });
    return coordinates === null || coordinates === void 0 ? void 0 : coordinates.pos;
};
// Event processors
const processPasteEvent = async (view, event) => {
    const clipboard = event.clipboardData;
    if (!clipboard)
        return false;
    if (clipboard.files.length > 0) {
        const handled = await handleFileContent(view, Array.from(clipboard.files));
        if (handled)
            event.preventDefault();
        return handled;
    }
    if (clipboard.types.includes("text/html")) {
        const html = clipboard.getData("text/html");
        const handled = await handleHtmlContent(view, html);
        if (handled)
            event.preventDefault();
        return handled;
    }
    return false;
};
const processDropEvent = async (view, event) => {
    const transfer = event.dataTransfer;
    if (!transfer)
        return false;
    const pos = getDropPosition(view, event);
    if (pos === undefined)
        return false;
    let handled = false;
    if (transfer.files.length > 0) {
        handled = await handleFileContent(view, Array.from(transfer.files), pos);
    }
    // Then try HTML content
    if (!handled && transfer.types.includes("text/html")) {
        const html = transfer.getData("text/html");
        handled = await handleHtmlContent(view, html, pos);
    }
    if (handled)
        event.preventDefault();
    return handled;
};
const hasImageContent = (dataTransfer) => {
    // Check for image files
    if (dataTransfer.files.length > 0 &&
        Array.from(dataTransfer.files).some(file => file.type.startsWith("image/"))) {
        return true;
    }
    // Check for HTML with image tags
    if (dataTransfer.types.includes("text/html")) {
        const html = dataTransfer.getData("text/html");
        if (html.includes("<img")) {
            return true;
        }
    }
    return false;
};
export function convertImageToData64() {
    return new Plugin({
        props: {
            handlePaste: (view, event) => {
                const clipboard = event.clipboardData;
                if (!clipboard)
                    return false;
                // Only handle paste events with image content
                if (hasImageContent(clipboard)) {
                    processPasteEvent(view, event);
                    return true;
                }
                // Not handling image content, let the editor handle it
                return false;
            },
            handleDrop: (view, event) => {
                const transfer = event.dataTransfer;
                if (!transfer)
                    return false;
                // Only handle drop events with image content
                if (hasImageContent(transfer)) {
                    processDropEvent(view, event);
                    return true;
                }
                // Not handling image content, let the editor handle it
                return false;
            },
        },
    });
}
